import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROTATE_MS = 5000;

/**
 * Rotating discount banner shown right below the Hero on the Home page.
 * Fetches active promotions from `/api/promotions/active` and rotates every 5s.
 * Auto-hides if there are no active promotions (returns null).
 */
export default function DiscountBanner() {
  const [promos, setPromos] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/promotions/active`)
      .then((r) => {
        if (alive) setPromos(Array.isArray(r.data) ? r.data : []);
      })
      .catch(() => {
        if (alive) setPromos([]);
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (promos.length <= 1) return;
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % promos.length);
    }, ROTATE_MS);
    return () => clearInterval(t);
  }, [promos.length]);

  if (promos.length === 0) return null;

  const p = promos[idx];
  const isPercent = p.discount_type === "percent";
  const valueText = isPercent
    ? `${Math.round(p.discount_value)}% de descuento`
    : `$${Number(p.discount_value).toLocaleString("es-MX")} MXN de descuento`;

  const propertyText =
    p.property_id === "casita"
      ? "La Casita"
      : p.property_id === "loft"
      ? "Loft Pura Brisa"
      : "en ambas propiedades";

  const validTo = new Date(p.valid_to + "T12:00:00").toLocaleDateString("es-MX", {
    day: "numeric",
    month: "long",
  });

  return (
    <section
      data-testid="discount-banner"
      className="relative bg-[#E07A5F] text-white overflow-hidden"
      aria-live="polite"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-3.5 md:py-4 flex flex-col md:flex-row items-center justify-center gap-x-6 gap-y-1 text-center md:text-left">
        <div className="hidden md:flex items-center justify-center w-8 h-8 rounded-full bg-white/20 shrink-0">
          <i className="fa-solid fa-tag text-[13px]" />
        </div>

        <div
          key={p.id}
          className="flex-1 flex flex-col md:flex-row md:items-baseline gap-x-4 gap-y-1 animate-fade-in"
          data-testid={`discount-slide-${idx}`}
        >
          <p className="font-serif-display text-lg md:text-xl leading-tight">
            <span className="font-bold">{valueText}</span>
            <span className="hidden md:inline text-white/80 mx-2">·</span>
            <span className="italic text-white/90">{p.label}</span>
          </p>
          <p className="text-[13px] uppercase tracking-[0.14em] text-white/80">
            {propertyText} · vigente hasta el {validTo}
          </p>
        </div>

        <a
          href="#villas"
          data-testid="discount-banner-cta"
          className="shrink-0 inline-flex items-center gap-2 bg-white text-[#E07A5F] px-5 py-2 text-xs uppercase tracking-[0.15em] font-semibold hover:bg-[#F2CC8F] hover:text-stone-900 transition-colors"
        >
          Reservar
          <i className="fa-solid fa-arrow-right text-[10px]" />
        </a>
      </div>

      {promos.length > 1 && (
        <div
          className="absolute bottom-0 left-0 right-0 flex justify-center gap-1.5 pb-1.5"
          data-testid="discount-banner-dots"
        >
          {promos.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              aria-label={`Ver promoción ${i + 1}`}
              className={`h-1 rounded-full transition-all ${
                i === idx ? "w-6 bg-white" : "w-1.5 bg-white/40 hover:bg-white/70"
              }`}
              data-testid={`discount-banner-dot-${i}`}
            />
          ))}
        </div>
      )}

      <style>{`
        @keyframes discount-fade-in {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in { animation: discount-fade-in 400ms ease-out; }
      `}</style>
    </section>
  );
}
